import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { DataSchema, FieldRelationType, FieldRole, FieldType } from './structured-data-service.types';
import { Observable, switchMap } from 'rxjs';
import { FileService } from '@bioconnect-ui/file-service';
import { FieldDescriptor, FileType } from '@bioconnect-ui/structured-data-service';
import { PagedResponse } from '@bioconnect-ui/shared-service';

export interface StructuredDataConfig {
  structuredDataServer: string;
  wijmoLicenseKey: string;
}

export enum FileEncoding {
  UTF8 = "utf-8",
  UTF8SIG = "utf-8-sig",
  USASCII = "us-ascii",
  ISO88591 = "iso-8859-1",
  CP1252 = "cp1252",
}

export enum FileFormat {
  CSV = "csv",
  XLSX = "xlsx",
  XLS = "xls",
  GSHEETS = "gsheets",
  JSON = "json",
  MARKDOWN = "markdown",
  ZIP = "zip",
}

export enum FileHashing {
  md5 = "md5",
}

export enum FileCompression {
  GZ = "gz",
  RAR = "rar",
  TAR = "tar",
  ZIP = "zip",
}

export enum FileScheme {
  BUFFER = "buffer",
  LOCAL = "local",
  MULTIPART = "multipart",
  REMOTE = "remote",
  STREAM = "stream",
  AWS = "aws",
}

@Injectable({
  providedIn: 'root'
})
/**
 * Service for interacting with the structured data API
 * Provides methods for managing file types, field descriptors, and data schemas
 */
export class StructuredDataService {
  /**
   * Creates an instance of StructuredDataService
   * @param httpClient - The Angular HttpClient for making API requests
   * @param env - Configuration for the structured data service
   */
  constructor(
    private httpClient: HttpClient,
    private fileService: FileService,
    @Inject('structuredDataServiceConfig') private env: StructuredDataConfig
  ) { }

  /**
   * Base URL for the structured data server API
   */
  private structuredDataServer = this.env.structuredDataServer;

  /**
   * Retrieves a paginated list of file types
   * @param skip - Number of records to skip for pagination
   * @param limit - Maximum number of records to return
   * @param filter - Optional filter string to search file types
   * @returns Observable of PagedResponse containing file types
   */
  getFileTypes(skip = 0, limit = 100, filter = ""): Observable<PagedResponse> {
    return this.httpClient.get<PagedResponse>(
      `${this.structuredDataServer}/api/file-types/?skip=${skip}&limit=${limit}&filter=${filter}`
    );
  }

  /**
   * Retrieves a specific file type by ID
   * @param id - The ID of the file type to retrieve
   * @returns Observable of FileType
   */
  getFileType(id: number): Observable<FileType> {
    return this.httpClient.get<FileType>(
      `${this.structuredDataServer}/api/file-types/${id}`
    );
  }

  /**
    * Create a new file type with a file
    */
  addFileTypeByFile(fileToUpload: File, type_name: string, measurement_types: number[]): Observable<any> {
    const formData: FormData = new FormData();
    formData.append('file', fileToUpload, fileToUpload.name);
    formData.append('type_name', type_name);
    formData.append('measurement_type_ids', JSON.stringify(measurement_types));
    return this.httpClient
      .post(
        `${this.structuredDataServer}/api/file-types/generate-from-file`,
        formData
      );
  }

  /**
    * Create a new file type 
    */
  addFileType(fileType: FileType): Observable<FileType> {
    return this.httpClient
      .post<FileType>(
        `${this.structuredDataServer}/api/file-types/`,
        fileType
      );
  }

  /**
   * Update an existing file type
   */
  updateFileType(fileType: FileType) {
    return this.httpClient.patch(
      `${this.structuredDataServer}/api/file-types/${fileType.id}`,
      fileType
    ).subscribe();
  }

  /**
   * Delete an existing file type
   */
  deleteFileType(id: number): Observable<any> {
    return this.httpClient
      .delete(
        `${this.structuredDataServer}/api/file-types/${id}`
      );
  }

  /**
   * Batch update an array of field descriptors 
   */
  updateFieldDescriptors(fieldDescriptors: FieldDescriptor[]):Observable<any> {
    return this.httpClient.patch(
      `${this.structuredDataServer}/api/field-descriptors/batch_update`,
      fieldDescriptors
    );
  }

  /**
   * Delete a specific field descriptor
   */
  deleteFieldDescriptor(id: number): void {
    this.httpClient.delete(
      `${this.structuredDataServer}/api/field-descriptors/${id}`
    ).subscribe();
  }

  /**
   * Validate a file based on a file type
   * @param fileName - Download name for file to validate
   * @param fileTypeId - The ID of the file type to validate against
   * @returns Observable of the validation result
   */
  validateDataFile(fileName: string, fileTypeId: number, userName: string): Observable<any> {
    return this.fileService.downloadFile(fileName).pipe(
      switchMap((fileBlob: Blob) => {
        const formData = new FormData();
        formData.append('file', fileBlob, fileName.split('/').pop() || fileName);
        formData.append('file_type_id', fileTypeId.toString());
        formData.append('user_name', userName);

        return this.httpClient.post(
          `${this.structuredDataServer}/api/validation-reports/validate-file`,
          formData
        );
      })
    );
  }

  /**
   * Validate a sample sheet based on it's template
   */
  validateSampleSheet(file: File, dataSchemaId: number): Observable<any> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    formData.append('data_schema_id', dataSchemaId.toString());
    return this.httpClient.post(
      `${this.structuredDataServer}/api/validation-reports/validate-by-schema`,
      formData
    );
  }

  /**
   * Create a new data schema using a data schema instance
   * @param dataSchema - The data schema to add
   * @returns data schema id
   */
  addDataSchema(dataSchema: DataSchema): Observable<any> {
    return this.httpClient.post(
      `${this.structuredDataServer}/api/data-schemas/`,
      dataSchema
    );
  }

   /**
    * Create a new file type with a file
    */
  addDataSchemaByFile(fileToUpload: File): Observable<any> {
    const formData: FormData = new FormData();
    formData.append('file', fileToUpload, fileToUpload.name);
    return this.httpClient
      .post(
        `${this.structuredDataServer}/api/data-schemas/generate-from-file`,
        formData
      );
  }


  /**
   * Create default Sample ID and Source ID field descriptors for a data schema
   * @param dataSchema - The data schema to add the field descriptors to
   * @returns the created field descriptors
   */
  createDefaultFieldDescriptors(dataSchemaId: number): Observable<any> {
    const sampleIdDescriptor: FieldDescriptor = {
      data_schema_id: dataSchemaId,
      name: 'Sample ID',
      type: FieldType.STRING,
      field_role: FieldRole.PRIMARY_IDENTIFIER,
      constraints: { required: true, unique: true },
    };

    const sourceIdDescriptor: FieldDescriptor = {
      data_schema_id: dataSchemaId,
      name: 'Source ID',
      type: FieldType.STRING,
      field_role: FieldRole.IDENTIFIER,
      relation_type: FieldRelationType.SOURCE_OF,
      related_to: ['Sample ID'],
      constraints: { required: true, unique: false },
    };

    const fieldDescriptors: FieldDescriptor[] = [sampleIdDescriptor, sourceIdDescriptor];

    return this.updateFieldDescriptors(fieldDescriptors);
  }


  /**
   * Retrieves a data schema by ID
   * @param data_schema_id - The ID of the data schema to retrieve
   * @returns Observable of DataSchema
   */
  getDataSchema(data_schema_id: number): Observable<DataSchema> {
    return this.httpClient.get<DataSchema>(
      `${this.structuredDataServer}/api/data-schemas/${data_schema_id}`
    );
  }

  /**
   * Retrieves a paginated list of data schemas
   * @param skip - Number of records to skip for pagination
   * @param limit - Maximum number of records to return
   * @param filter - Optional filter string to search data schemas
   * @returns Observable of PagedResponse containing data schemas
   */
  getDataSchemas(skip = 0, limit = 100, filter = ""): Observable<PagedResponse> {
    return this.httpClient.get<PagedResponse>(
      `${this.structuredDataServer}/api/data-schemas/?skip=${skip}&limit=${limit}&filter=${filter}`
    );
  }

  /**
   * Delete an existing file type
   */
  deleteDataSchema(id: number): Observable<any> {
    return this.httpClient
      .delete(
        `${this.structuredDataServer}/api/data-schemas/${id}`
      );
  }

}